import { Menu } from '@base-ui/react/menu';
import styled, { css, keyframes } from 'styled-components';
import { PORTAL_Z_INDEX } from 'modules/v2/foundation/PortalProvider/styled';
import { breakpoints } from 'modules/v2/foundation/breakpoints';
import { pxToRem } from 'modules/v2/foundation/spacing';
import { themeGet } from 'modules/v2/foundation/theme';
import { widthProps } from 'modules/v2/utils/responsiveProperty';
export const HeaderContainer = styled.div `
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing);
  padding: ${themeGet('spacing.md')} ${themeGet('spacing.lg')};
  border-bottom: solid ${pxToRem(1)} ${themeGet('colors.border.100')};

  ${(props) => props.$sticky &&
    css `
      position: sticky;
      z-index: 1;
      top: 0;
      background-color: ${themeGet('colors.background.50')};
    `}
`;
export const FooterContainer = styled.div `
  display: flex;
  flex-direction: column;
  margin-top: var(--spacing);
  border-top: solid ${pxToRem(1)} ${themeGet('colors.border.100')};

  padding: ${(props) => (props.$withoutPadding ? 0 : themeGet('spacing.md'))};

  ${(props) => props.$sticky &&
    css `
      position: sticky;
      z-index: 1;
      bottom: 0;
      background-color: ${themeGet('colors.background.50')};
    `}
`;
export const LabelContainer = styled.div `
  display: flex;
  align-items: center;
  gap: ${themeGet('spacing.lg')};
  margin-left: var(--spacing);
  padding: var(--spacing) ${themeGet('spacing.md')};

  &:first-child {
    margin-top: var(--spacing);
  }

  &:last-child {
    margin-bottom: var(--spacing);
  }

  ${(props) => props.$sticky &&
    css `
      position: sticky;
      top: 0;
      background-color: ${themeGet('colors.background.50')};
    `}
`;
export const SearchContainerIcon = styled.div `
  width: var(--icon-size);
  height: var(--icon-size);
  display: flex;
  align-items: center;
  justify-content: center;

  [data-icon='falcon'] {
    color: ${themeGet('colors.gray.500')};
    transition: color 175ms ease-in-out;
  }

  &:empty {
    display: none;
  }
`;
export const SearchContainer = styled(HeaderContainer) `
  --icon-size: ${pxToRem(16)};

  position: relative;
  border-bottom: solid ${pxToRem(1)} ${themeGet('colors.border.100')};
  padding-top: 0;
  padding-bottom: 0;

  gap: ${themeGet('spacing.md')};

  &:has(input[data-highlighted]) {
    ${SearchContainerIcon} {
      & > [data-icon='falcon'] {
        color: ${themeGet('colors.gray.800')};
      }
    }
  }

  ${(props) => props.$sticky &&
    css `
      position: sticky;
      top: 0;
      background-color: ${themeGet('colors.background.50')};
    `}
`;
export const SearchContainerInput = styled.input `
  height: ${pxToRem(40)};
  width: 100%;
  border: none;
  outline: none;
  padding: 0;

  color: ${themeGet('colors.text.300')};
  font-size: ${themeGet('fontSize.small')};

  &::placeholder {
    color: ${themeGet('colors.gray.400')};
    transition: color 175ms ease-in-out;
  }

  &:hover:not(:disabled) {
    &::placeholder {
      color: ${themeGet('colors.text.200')};
    }
  }
`;
/**
 * Styles for toggle items
 */
export const CheckboxContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};

  opacity: 0;
  transition: opacity 175ms ease-in-out;

  &[data-checked='true'] {
    opacity: 1;
  }
`;
export const SwitchContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding-left: ${themeGet('spacing.md')};
`;
/**
 * Content styles for Menu and MenuNested
 */
const fadeInAnimation = keyframes `
  from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;
const ContentStyles = css `
  --spacing: ${themeGet('spacing.sm')};

  box-sizing: content-box; // This is so we calculate the height correctly with the border for inherited heights

  max-height: min(var(--available-height), 60vh);
  border-radius: ${themeGet('borderRadius.md')};
  border: solid ${pxToRem(1)} ${themeGet('colors.border.100')};
  background-color: ${themeGet('colors.background.50')};
  box-shadow: ${themeGet('dropShadow.300')};

  will-change: opacity;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  animation-name: ${fadeInAnimation};
`;
export const StyledPositioner = styled(Menu.Positioner) `
  z-index: ${PORTAL_Z_INDEX}; // This is so the menu is aligning with the other components that use the PortalProvider
`;
export const StyledContent = styled(Menu.Popup).attrs((props) => (Object.assign({ minWidth: pxToRem(164), maxWidth: pxToRem(320) }, props))) `
  ${ContentStyles};
  ${widthProps};

  ${breakpoints.xs} {
    width: var(--available-width);
    max-width: var(--available-width);
    max-height: var(--available-height);
  }

  ${(props) => props.$useTriggerWidth &&
    css `
      width: var(--anchor-width);
      max-width: var(--anchor-width);
    `}
`;
export const StyledSubContent = styled(Menu.Popup).attrs((props) => (Object.assign({ minWidth: pxToRem(260), maxWidth: pxToRem(320) }, props))) `
  ${ContentStyles};
  ${widthProps};

  ${breakpoints.xs} {
    animation: none;
    transform: translateX(calc(var(--anchor-width) + ${pxToRem(5)}));
    width: calc(var(--anchor-width));
    max-width: calc(var(--anchor-width));
    max-height: var(--available-height);
  }
`;
/**
 * Item styles for MenuItem and MenuItemNested
 */
const ItemStyles = css `
  display: flex;
  align-items: center;
  gap: ${themeGet('spacing.md')};

  color: ${themeGet('colors.text.300')};
  font-size: ${themeGet('fontSize.small')};
  line-height: ${pxToRem(14)};

  padding: ${pxToRem(6)} ${themeGet('spacing.md')};
  margin: 0 var(--spacing);

  outline: none;
  user-select: none;
  border-radius: ${themeGet('borderRadius.sm')};
  transition: background-color 175ms ease-in-out;

  &:hover:not([data-disabled]),
  &:focus-within:not([data-disabled]) {
    cursor: pointer;

    background-color: ${themeGet('colors.background.100')};

    ${CheckboxContainer} {
      opacity: 1;
    }
  }

  &[data-disabled] {
    opacity: 0.5;
  }

  & > [data-icon='falcon'] {
    --falcon-size: ${pxToRem(12)};

    color: ${themeGet('colors.primary.500')};
  }

  &:first-of-type {
    margin-top: var(--spacing);
  }

  &:last-of-type {
    margin-bottom: var(--spacing);
  }
`;
export const InputContainer = styled.div `
  ${ItemStyles};
  padding-top: 0;
`;
export const StyledItem = styled(Menu.Item) `
  ${ItemStyles};
`;
export const StyledSubTrigger = styled(Menu.SubmenuTrigger) `
  ${ItemStyles};
`;
export const IconContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};
  margin-left: auto;
`;
/**
 * Styles for MenuDivider
 */
export const StyledSeparator = styled(Menu.Separator) `
  height: ${pxToRem(1)};
  margin: var(--spacing) 0;
  background-color: ${themeGet('colors.border.100')};
`;
/**
 * Styles for MenuItemNested
 */
export const NestedItemIcon = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${pxToRem(16)};
  height: ${pxToRem(16)};
  margin-left: auto;

  [data-icon='falcon'] {
    color: ${themeGet('colors.gray.400')};
  }
`;
